import React, { useRef, useEffect } from 'react';
import { motion, useDragControls } from 'framer-motion';

const Window = ({ id, title, children, isOpen, onClose, onMinimize, zIndex, onFocus, position, size, onResize, constraintsRef, scrollable = true }) => {
    const windowRef = useRef(null);
    const dragControls = useDragControls();

    useEffect(() => {
        if (isOpen && windowRef.current) {
            // Ensure initial size is applied if provided
            if (size?.width) windowRef.current.style.width = `${size.width}px`;
            if (size?.height && size.height !== 'auto') windowRef.current.style.height = `${size.height}px`;
        }
    }, [isOpen, size]);

    if (!isOpen) return null;

    const handleResizeMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const startX = e.clientX;
        const startY = e.clientY;
        const startWidth = windowRef.current.offsetWidth;
        const startHeight = windowRef.current.offsetHeight;

        const handleMouseMove = (moveEvent) => {
            const newWidth = startWidth + (moveEvent.clientX - startX);
            const newHeight = startHeight + (moveEvent.clientY - startY);

            // Enforce minimum size
            const constrainedWidth = Math.max(300, newWidth);
            const constrainedHeight = Math.max(200, newHeight);

            // Direct DOM update for performance (avoids React render cycle)
            windowRef.current.style.width = `${constrainedWidth}px`;
            windowRef.current.style.height = `${constrainedHeight}px`;
        };

        const handleMouseUp = (upEvent) => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);

            // Sync final size with React state
            const finalWidth = Math.max(300, startWidth + (upEvent.clientX - startX));
            const finalHeight = Math.max(200, startHeight + (upEvent.clientY - startY));

            onResize(id, { width: finalWidth, height: finalHeight });
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <motion.div
            ref={windowRef}
            drag
            dragListener={false}
            dragControls={dragControls}
            dragMomentum={false}
            dragConstraints={constraintsRef}
            initial={{ x: position?.x || 100, y: position?.y || 100, scale: 0.8, opacity: 0 }}
            animate={{
                scale: 1,
                opacity: 1
            }}
            exit={{ scale: 0.8, opacity: 0 }}
            onMouseDown={() => onFocus(id)}
            style={{
                zIndex,
                width: size?.width,
                height: size?.height
            }}
            className="absolute bg-[#e8e6df] rounded-xl shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5),0_0_0_1px_rgba(0,0,0,0.1),inset_0_1px_0_rgba(255,255,255,0.5)] flex flex-col font-sans"
        >
            {/* Inner Bevel */}
            <div className="absolute inset-0 rounded-xl border border-white/50 pointer-events-none"></div>

            {/* Title Bar */}
            <div
                onPointerDown={(e) => dragControls.start(e)}
                className="px-4 py-3 flex items-center justify-between select-none flex-shrink-0 relative z-10"
            >
                <div className="flex space-x-2">
                    <button
                        onClick={() => onClose(id)}
                        className="w-3 h-3 rounded-full bg-[#ff5f57] shadow-[inset_0_1px_2px_rgba(0,0,0,0.2),0_1px_0_rgba(255,255,255,0.5)] hover:brightness-90 active:brightness-75 transition-all"
                    ></button>
                    <button
                        onClick={() => onMinimize && onMinimize(id)}
                        className="w-3 h-3 rounded-full bg-[#febc2e] shadow-[inset_0_1px_2px_rgba(0,0,0,0.2),0_1px_0_rgba(255,255,255,0.5)] hover:brightness-90 active:brightness-75 transition-all"
                    ></button>
                </div>
                <div className="font-bold text-[10px] uppercase tracking-widest text-[#444444] drop-shadow-[0_1px_0_rgba(255,255,255,0.8)]">{title}</div>
                <div className="w-8"></div> {/* Spacer */}
            </div>

            {/* Window Content */}
            <div className="px-1 pb-1 flex-1 relative overflow-hidden flex flex-col">
                <div className={`bg-[#dcdad3] rounded-lg border border-black/5 shadow-[inset_0_2px_4px_rgba(0,0,0,0.05)] h-full relative z-10 ${scrollable ? 'overflow-auto' : 'overflow-hidden'}`}>
                    {children}
                </div>

                {/* Resize Handle */}
                <div
                    onMouseDown={handleResizeMouseDown}
                    className="absolute bottom-1 right-1 w-6 h-6 cursor-nwse-resize flex items-center justify-center z-50 opacity-50 hover:opacity-100"
                >
                    <div className="w-2 h-2 border-b-2 border-r-2 border-[#999999]"></div>
                </div>
            </div>
        </motion.div>
    );
};

export default Window;
